import { useEffect } from 'react';
import { Outlet, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Nav from '@/components/Nav';
import ErrorBoundary from '@/components/ErrorBoundary';
import PortalChat from '@/features/portal-chat/PortalChat';
import { useAuth } from '@/lib/auth/useAuth';
import { registerAuthSource } from '@/lib/analytics/tracker';
import { usePageViewTracking } from '@/lib/analytics/usePageViewTracking';
import styles from './App.module.css';

/**
 * App shell: Nav → main → footer.
 *
 * Footer styles live in `App.module.css` rather than a global stylesheet —
 * a global stylesheet regressed every page +2-7pp due to cascade conflicts
 * with Tailwind, so do not re-add one.
 *
 * Admin footer links render only for analytics admins.
 *
 * This shell also wires user-activity tracking: it registers the shared
 * query client with the analytics tracker (so it can read auth state) and
 * mounts `usePageViewTracking` once, inside the router context.
 */
export default function App(): JSX.Element {
  const { isAnalyticsAdmin } = useAuth();
  const queryClient = useQueryClient();
  const currentYear = new Date().getFullYear();

  // Give the (non-React) activity tracker access to the auth cache so it can
  // gate events on an authenticated user.
  useEffect(() => {
    registerAuthSource(queryClient);
  }, [queryClient]);

  // Fire a `page_view` activity event on every route change.
  usePageViewTracking();

  return (
    <>
      <Nav />
      <main>
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>
      <footer className={styles.siteFooter}>
        <div className={styles.footerContainer}>
          <img
            src="/static/logos/image.png"
            alt="Fulcrum Asset Management"
            className={styles.footerLogo}
          />
          <p>Copyright © Fulcrum Asset Management LLP {currentYear}. All rights reserved.</p>
          <p className={styles.footerLinks}>
            <Link to="/disclaimer" className={styles.footerLink}>Disclaimer</Link>
            {isAnalyticsAdmin ? (
              <>
                <span className={styles.footerSeparator}>|</span>
                <Link to="/admin/login-analytics" className={styles.footerLink}>Login Analytics</Link>
                <span className={styles.footerSeparator}>|</span>
                <Link to="/admin/user-activity" className={styles.footerLink}>User Activity</Link>
              </>
            ) : null}
          </p>
        </div>
      </footer>
      {/* Floating MARS chat — gated to MARS routes inside the component
          so the widget vanishes on Central Bank / Admin / Dashboard. */}
      <PortalChat />
    </>
  );
}
